import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

// Open/closed state for the mobile off-canvas navigation drawer. The hamburger
// lives in Header and the drawer is SideMenu, so the state has to sit above
// both. Desktop never opens it (the hamburger is display:none there).
interface MobileNavContextType {
  isNavOpen: boolean;
  openNav: () => void;
  closeNav: () => void;
  toggleNav: () => void;
}

const MobileNavContext = createContext<MobileNavContextType>({
  isNavOpen: false,
  openNav: () => {},
  closeNav: () => {},
  toggleNav: () => {},
});

export const MobileNavProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isNavOpen, setIsNavOpen] = useState(false);

  const openNav = useCallback(() => setIsNavOpen(true), []);
  const closeNav = useCallback(() => setIsNavOpen(false), []);
  const toggleNav = useCallback(() => setIsNavOpen((open) => !open), []);

  // Lock body scroll while the drawer is open so the page doesn't pan behind
  // it on touch devices.
  useEffect(() => {
    if (!isNavOpen) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isNavOpen]);

  useEffect(() => {
    if (!isNavOpen) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsNavOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isNavOpen]);

  return (
    <MobileNavContext.Provider value={{ isNavOpen, openNav, closeNav, toggleNav }}>
      {children}
    </MobileNavContext.Provider>
  );
};

export const useMobileNav = () => useContext(MobileNavContext);
